import axios from 'axios';
import { getAccessTokenJWT } from 'utils/okta';
import { appConfig } from 'config/appConfig';
import { epochToISOFormat } from 'utils/date';
import { getCountry } from 'utils/country';
import { tableHeaders } from 'constants/userLibrary';
import okta from 'constants/okta';

const APIEndpoint = 'needsassessments';
let controller = new AbortController();

export const save = async (company, project, na) => {
  const {
    leaseExpirationYear,
    size,
    headcount,
    inOffice,
    fullyRemote,
    hybrid,
    daysInOffice,
    defaultGrowth,
    startingYear,
    leaseTermInYears,
    spaces,
    growth,
    workplacestyleSelected,
    sharedSeatsRatio,
  } = na;

  const CompanyEdpid = company.isEdpCompany ? company.id : null;
  const CompanyId = !company.isEdpCompany ? company.id : null;

  const payload = {
    Name: project,
    CompanyName: company.name,
    CompanyEdpid: CompanyEdpid,
    CompanyId: CompanyId,
    LeaseExpiration: leaseExpirationYear ? epochToISOFormat(leaseExpirationYear) : null,
    Size: size,
    StartingYear: epochToISOFormat(startingYear),
    LeaseTermYears: leaseTermInYears,
    Headcount: headcount,
    InOffice: inOffice,
    FullyRemote: fullyRemote,
    Hybrid: hybrid,
    DaysInOffice: daysInOffice,
    DefaultGrowth: defaultGrowth,
    WorkplaceStyleSelected: workplacestyleSelected,
    Workplaces: JSON.stringify(spaces),
    Growth: JSON.stringify(growth),
    SharedSeatsRatio: JSON.stringify(sharedSeatsRatio),
    Country: getCountry(),
    CreatedAt: epochToISOFormat(new Date(), true),
    LastStep: 1,
    ScoreCardReached: false,
  };

  return axios({
    method: 'post',
    url: appConfig.API_URL + '/' + APIEndpoint,
    data: payload,
    headers: {
      Authorization: okta.Bearer + getAccessTokenJWT(),
      'subscription-key': appConfig.API_SUBSCRIPTION_KEY,
    },
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      // TO-DO. Logging
      // control 401. okta redirection
      console.error(error);
      throw Error(error);
    });
};

export const update = async (na) => {
  const {
    id,
    leaseExpirationYear,
    size,
    headcount,
    inOffice,
    fullyRemote,
    hybrid,
    daysInOffice,
    defaultGrowth,
    startingYear,
    leaseTermInYears,
    workplacestyleSelected,
    spaces,
    growth,
    anyonecanaccess,
    lastStep,
    scoreCardReached,
    sharedSeatsRatio,
  } = na;

  const payload = {
    Id: id,
    LeaseExpiration: leaseExpirationYear ? epochToISOFormat(leaseExpirationYear) : null,
    Size: size,
    StartingYear: epochToISOFormat(startingYear),
    LeaseTermYears: leaseTermInYears,
    Headcount: headcount,
    InOffice: inOffice,
    FullyRemote: fullyRemote,
    Hybrid: hybrid,
    DaysInOffice: daysInOffice,
    DefaultGrowth: defaultGrowth,
    WorkplaceStyleSelected: workplacestyleSelected,
    Workplaces: JSON.stringify(spaces),
    Growth: JSON.stringify(growth),
    SharedSeatsRatio: JSON.stringify(sharedSeatsRatio),
    Country: getCountry(),
    AnyoneCanAccess: anyonecanaccess,
    UpdatedAt: epochToISOFormat(new Date(), true),
    LastStep: lastStep,
    ScoreCardReached: scoreCardReached,
  };

  return axios({
    method: 'put',
    url: appConfig.API_URL + '/' + APIEndpoint,
    data: payload,
    headers: {
      Authorization: okta.Bearer + getAccessTokenJWT(),
      'subscription-key': appConfig.API_SUBSCRIPTION_KEY,
    },
  })
    .then((response) => response.data)
    .catch((error) => {
      // TO-DO. Logging
      // control 401. okta redirection
      console.error(error);
      throw Error(error);
    });
};

export const search = async (
  searchText,
  sortField,
  sortOrder,
  pageNumberActive,
  pageNumberDeleted,
  pageSize,
) => {
  controller.abort();
  controller = new AbortController();

  const payload = {
    SearchText: searchText,
    SortField: sortField || tableHeaders.lastModified.sortField,
    SortOrder: sortOrder || 'desc',
    Country: getCountry(),
    PageNumberActive: pageNumberActive,
    PageNumberDeleted: pageNumberDeleted,
    PageSize: pageSize,
  };

  return axios({
    method: 'post',
    signal: controller.signal,
    url: appConfig.API_URL + '/' + APIEndpoint + '/search',
    data: payload,
    headers: {
      Authorization: okta.Bearer + getAccessTokenJWT(),
      'subscription-key': appConfig.API_SUBSCRIPTION_KEY,
    },
  })
    .then((response) => {
      return response.data;
    })

    .catch((error) => {
      console.error(error);
      throw Error(error);
    });
};

export const deleteOrRestoreAssessment = (id, isDeleted) => {
  return axios({
    method: 'delete',
    url: appConfig.API_URL + '/' + APIEndpoint,
    data: {
      NeedsAssessmentsId: id,
      IsDeleted: isDeleted,
      Country: getCountry(),
    },
    headers: {
      Authorization: okta.Bearer + getAccessTokenJWT(),
      'subscription-key': appConfig.API_SUBSCRIPTION_KEY,
    },
  })
    .then((response) => response.data)
    .catch((error) => {
      console.error(error);
      return Promise.reject(error);
    });
};

export const get = (id, apiToken) => {
  return axios({
    method: 'get',
    url: appConfig.API_URL + '/' + APIEndpoint,
    params: {
      id,
      country: getCountry(),
    },
    headers: {
      Authorization: apiToken ? apiToken : okta.Bearer + getAccessTokenJWT(),
      'subscription-key': appConfig.API_SUBSCRIPTION_KEY,
    },
  })
    .then((response) => response.data)
    .catch((error) => {
      console.error(error);
      return Promise.reject(error);
    });
};

export const getOwner = (id, apiToken) => {
  return axios({
    method: 'get',
    url: appConfig.API_URL + '/' + APIEndpoint + '/getowner',
    params: {
      id,
      country: getCountry(),
    },
    headers: {
      Authorization: apiToken ? apiToken : okta.Bearer + getAccessTokenJWT(),
      'subscription-key': appConfig.API_SUBSCRIPTION_KEY,
    },
  })
    .then((response) => response.data)
    .catch((error) => {
      console.error(error);
      return Promise.reject(error);
    });
};

export const copy = async (naCopy) => {
  const { originalAssessmentId, assessmentName, companyName } = naCopy;

  const payload = {
    OriginalAssessmentId: originalAssessmentId,
    AssessmentName: assessmentName,
    CompanyName: companyName,
    Country: getCountry(),
    CreatedAt: epochToISOFormat(new Date(), true),
  };

  return axios({
    method: 'post',
    url: appConfig.API_URL + '/' + APIEndpoint + '/copy',
    data: payload,
    headers: {
      Authorization: okta.Bearer + getAccessTokenJWT(),
      'subscription-key': appConfig.API_SUBSCRIPTION_KEY,
    },
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      // TO-DO. Logging
      // control 401. okta redirection
      console.error(error);
      throw Error(error);
    });
};
